/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Report, Stage, HistoryStage, Helpers} from '../model';
import {BehaviorSubject, Subject} from 'rxjs';
import { Injectable, EventEmitter} from '@angular/core';
import {RecentReportService} from './recent-reports.service';
import {ReportHistoryProvider} from '../providers';
import {StompClient} from './stomp.client';
import {ReportNotification} from '../model/report.notification';
import { StageChangeRequest, ReportUsers, AssigneeType, ReportUserType } from '../model/report';
import { UserPersistenceService } from './user.persistence.service';
import { ModeService } from './mode.service';
import { SystemType } from '../model/enums/system.type';
import { ConfirmationDialogComponent } from '../components/fields';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { LogService } from 'src/app/services';

@Injectable()
export class ReportService {
  report = new BehaviorSubject<Report>(null);
  report$ = this.report.asObservable();

  reportStages = new BehaviorSubject<Stage[]>([]);
  reportStages$ = this.reportStages.asObservable();

  reportCharges = new BehaviorSubject<any>('');
  reportCharges$ = this.reportCharges.asObservable();

  reportNotifications = new BehaviorSubject<ReportNotification>(null);
  reportNotifications$ = this.reportNotifications.asObservable();

  reportLock = new BehaviorSubject<boolean>(true);
  reportLock$ = this.reportLock.asObservable();

  reportDcfEnable = new BehaviorSubject<boolean>(false);
  reportDcfEnable$ = this.reportDcfEnable.asObservable();

  updateDateEngaged: EventEmitter<void> = new EventEmitter();

  convert: EventEmitter<void> = new EventEmitter();
  updateQuote: EventEmitter<void> = new EventEmitter();

  currentMode: SystemType;
  confirmationModal: BsModalRef;

  constructor(
    private modalService: BsModalService,
    private recentReportsService: RecentReportService,
    public historyProvider: ReportHistoryProvider,
    private stompClient: StompClient,
    private userPersistenceService: UserPersistenceService,
    private modeService: ModeService,
    public logService : LogService
  ) {
    this.stompClient.subscribe('/reports')
      .subscribe((message) => this.reportNotifications.next(ReportNotification.parse(message)));

    this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  setReport(report: Report): void {
    this.report.next(report);

    // Load Stage History
    if (report != null)
      this.loadStageHistory(report.ID);

    // Add Report to Recent History
    this.recentReportsService.addToHistory(report);
  }

  clearReport(): void {
    this.report.next(null);
    this.reportStages.next(null);
  }

  addStageHistory(h: HistoryStage): void {
    if (this.reportStages.getValue()) {
      for (const s of this.reportStages.getValue())
        if (s.ID === h.SID)
          s.HISTORY.push(h);
    }
  }

  private loadStageHistory(id: string): void {
    this.historyProvider.getStageHistory(id, this.currentMode)
      .subscribe(data => {
        if (data.success) {
          this.reportStages.next(this.userPersistenceService.persistReportStageUserAndAssignee(data.result));
        } else this.logService.error(data.error);
      }, err => {
        this.logService.error(err);
      });
  }

  setReportLock(value: boolean): void {
    this.reportLock.next(value);
  }

  setReportDcf(value: boolean): void {
    this.reportDcfEnable.next(value);
  }
  getReportStageRequest(nextStageId: string, userList: ReportUsers): StageChangeRequest {
    const stageRequest: StageChangeRequest = new StageChangeRequest();
    switch (nextStageId) {
    case 'INDI':
    case 'QUOT':
    case 'ORDR':
    case 'FBRI':
    case 'ELST':
    case 'ENGD':
      if (userList?.PROCESSOR) {
        stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
        stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.PROCESSOR;
        stageRequest.ASSIGNEE_ID = null;
      } else {
        stageRequest.ASSIGNEE_TYPE_USER = null;
      }
      stageRequest.reportUserType = ReportUserType.PROCESSOR;
      break;
    case 'STRT':
      if (userList?.RESEARCH_ANALYST) {
        stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
        stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.RESEARCH_ANALYST;
        stageRequest.ASSIGNEE_ID = null;
      } else {
        stageRequest.ASSIGNEE_TYPE_USER = null;
      }
      stageRequest.reportUserType = ReportUserType.RESEARCH_ANALYST;
      break;
    case 'SPST':
    case 'SPRE':
    case 'R1DR':
    case 'RDY2':
    case 'HLRC':
      if (userList?.FINANCIAL_ANALYST) {
        stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
        stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.FINANCIAL_ANALYST;
        stageRequest.ASSIGNEE_ID = null;
      } else {
        stageRequest.ASSIGNEE_TYPE_USER = null;
      }
      stageRequest.reportUserType = ReportUserType.FINANCIAL_ANALYST;
      break;
    case 'REVW':
    case 'REV2':
      if (userList?.SENIOR_ANALYST) {
        stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
        stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.SENIOR_ANALYST;
        stageRequest.ASSIGNEE_ID = null;
      } else {
        stageRequest.ASSIGNEE_TYPE_USER = null;
      }
      stageRequest.reportUserType = ReportUserType.SENIOR_ANALYST;
      break;
    case 'RDY1':
    case 'VLCK':
      stageRequest.ASSIGNEE_TYPE_USER = null;
      // Senior Reviewer - Rohul Patel
      /*stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
      stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.SENIOR_REVIEWER;
      stageRequest.ASSIGNEE_ID = null;*/
      break;
    case 'FINR':
      stageRequest.ASSIGNEE_TYPE_USER = null;
      // Senior Appraiser - Neal Patel
      /*stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
      stageRequest.ASSIGNEE_TYPE_USER = ReportUserType.SENIOR_APPRAISER;
      stageRequest.ASSIGNEE_ID = null;*/
      break;
    }

    stageRequest.ID = nextStageId;

    return stageRequest;
  }

  isDisabled(): boolean {
    return this.reportLock.getValue() ? !!this.report.getValue().DOCUMENTS.length || this.report.getValue().STAGE.ORDER >= 5 : false;
  }

  isActiveEdit(): boolean {
    return this.report.getValue().STATE.ID !== 'CCD';
  }

  isBVQActivateEdit(): boolean {
    return this.report.getValue().STATE.ID !== 'CCD' &&
    this.report.getValue().STATE.ID !== 'LST' &&
    this.report.getValue().STATE.ID !== 'CMP';
  }

  showSuccessDialog(): void {
    const config = Helpers.getModalConfig();
    config.class = 'modal-dialog-centered';

    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, config);
    const ackSubject = new Subject<any>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Success';
    this.confirmationModal.content.text = 'Email has been successfully sent!';
    this.confirmationModal.content.actionButton = 'Ok';
    this.confirmationModal.content.showCancel = false;

    ackSubject.subscribe(value => {
      if(value){
      }
    });
  }
}
